import { getLoginUrl } from "@/config/v2Migration";

// A token-triggered re-login is skipped if one already happened this recently,
// so a skewed client clock cannot cause an endless login loop.
const RELOGIN_GUARD_MS = 30_000;
const RELOGIN_AT_KEY = "reloginAt";

/**
 * Returns the `exp` claim (seconds) of a JWT without verifying it, or undefined
 * when the token is not a decodable JWT with a numeric `exp`.
 */
export const getJwtExp = (token: string): number | undefined => {
	const payload = token.split(".")[1];
	if (!payload) return undefined;
	try {
		const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
		const json: unknown = JSON.parse(atob(base64));
		if (typeof json === "object" && json !== null && "exp" in json) {
			return typeof json.exp === "number" ? json.exp : undefined;
		}
	} catch {
		// not a JWT
	}
	return undefined;
};

/** True when the access token is present and its `exp` is still in the future. */
export const isAccessTokenUsable = (
	token: string,
	nowSeconds: number = Date.now() / 1000,
): boolean => {
	const exp = getJwtExp(token);
	return exp !== undefined && exp > nowSeconds;
};

/**
 * Navigates to the login page, remembering `requestedPath` to return to after login.
 * Same steps as the router's original login redirect.
 */
export const redirectToLogin = (requestedPath: string): void => {
	document.cookie = `requested_path=${requestedPath}`;
	window.name = "init";
	window.location.href = getLoginUrl();
};

/**
 * Re-login caused by an expired / rejected token. Skipped (returns false) when another
 * re-login started within the last 30 s, so a wrong clock or a token the server keeps
 * rejecting cannot loop forever.
 */
export const redirectToReLogin = (requestedPath: string): boolean => {
	const now = Date.now();
	let last = Number.NaN;
	try {
		last = Number(sessionStorage.getItem(RELOGIN_AT_KEY));
	} catch {
		// storage unavailable: no guard
	}
	if (now - last < RELOGIN_GUARD_MS) return false;
	try {
		sessionStorage.setItem(RELOGIN_AT_KEY, String(now));
	} catch {
		// storage unavailable: no guard
	}
	redirectToLogin(requestedPath);
	return true;
};

/** Current location as a router path (without the app's base URL). */
export const currentRouterPath = (): string => {
	const base = import.meta.env.BASE_URL.replace(/\/$/, "");
	const { pathname, search, hash } = window.location;
	const path =
		base && pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
	return `${path || "/"}${search}${hash}`;
};

/**
 * `fetch` for v2 (Hono `hc`) clients: a 401 starts a re-login. The response is still
 * returned so callers keep their existing error handling.
 */
export const fetchWithLoginRedirect: typeof fetch = async (input, init) => {
	const res = await fetch(input, init);
	if (res.status === 401) {
		redirectToReLogin(currentRouterPath());
	}
	return res;
};
